import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { AsyncPipe, KeyValuePipe, TitleCasePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { combineLatest, takeUntil } from 'rxjs';

import { CityDetails } from 'src/app/core/types/models/city-details.model';
import { CityResponse } from 'src/app/core/types/models/city-response.model';
import { CountryDetails } from 'src/app/core/types/models/country-details.model';
import { CountryResponse } from 'src/app/core/types/models/country-response.model';
import { OccasionGeniusResponse } from 'src/app/core/types/models/occasion-genius-response.model';

import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { ERoutes } from 'src/app/core/enums/routes.enum';
import { EInfoType } from 'src/app/core/enums/country-info-type.enum';
import {
  IViatorCategories,
  ViatorComponent,
} from 'src/app/shared/used-components/viator/viator.component';

import { ThingsToDoService } from '../../../services/things-to-do.service';
import { ScrollToIdService } from 'src/app/core/services/scroll-to-id.service';
import { CountryInfoService } from 'src/app/core/services/country-info.service';
import { VoiceMapResponse } from 'src/app/core/types/models/voice-map-response';
import { CountryInfoTextService } from '../../../services/country-info-text.service';
import { OccasionGeniusService } from 'src/app/core/services/occasion-genius.service';
import { HotelsProviderServiceService } from 'src/app/shared/used-components/hotels-booking-container/hotels-provider-service.service';

import { ReplaceStrPipe } from 'src/app/shared/pipes/replace-str.pipe';
import { ParseCoordinatesPipe } from 'src/app/shared/pipes/parse-coordinates.pipe';

import { SwiperDirective } from 'src/app/shared/directives/swiper.directive';

import { PageInfoComponent } from '../../../pages/page-info';
import { SimpleMapComponent } from 'src/app/shared/maps/simple-map/simple-map.component';
import { AudioToursComponent } from 'src/app/shared/used-components/audio-tours/audio-tours.component';
import { TextEllipsisComponent } from 'src/app/shared/components/text-ellipsis/text-ellipsis.component';
import { SimpleBreadcrumbComponent } from '../../../components/simple-breadcrumb/simple-breadcrumb.component';
import { OccasionGeniusComponent } from 'src/app/shared/components/occasion-genius/occasion-genius.component';
import { GalleryMobileComponent } from '../../../../countries/components/mobile/gallery-mobile/gallery-mobile.component';
import { StartPlaningMobileComponent } from 'src/app/shared/components/start-planing-mobile/start-planing-mobile.component';
import { ItineraryListMobileComponent } from '../../../../countries/components/mobile/itinerary-list-mobile/itinerary-list-mobile.component';
import { AttractionListMobileComponent } from '../../../../countries/components/mobile/attraction-list-mobile/attraction-list-mobile.component';
import {
  HOTEL_CLIENT,
  HotelsBookingContainerComponent,
} from 'src/app/shared/used-components/hotels-booking-container/hotels-booking-container.component';

import { SwiperOptions } from 'swiper/types/swiper-options';

@Component({
  selector: 'app-things-to-do-mobile',
  templateUrl: './things-to-do-mobile.component.html',
  styleUrls: ['./things-to-do-mobile.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    CountryInfoService,
    ThingsToDoService,
    CountryInfoTextService,
    OccasionGeniusService,
    {
      provide: HOTEL_CLIENT,
      useClass: HotelsProviderServiceService,
    },
  ],
  imports: [
    TranslateModule,
    ReplaceStrPipe,
    TitleCasePipe,
    SimpleBreadcrumbComponent,
    NgxSkeletonLoaderModule,
    AsyncPipe,
    KeyValuePipe,
    RouterModule,
    SimpleMapComponent,
    HotelsBookingContainerComponent,
    ParseCoordinatesPipe,
    ViatorComponent,
    OccasionGeniusComponent,
    AudioToursComponent,
    GalleryMobileComponent,
    ItineraryListMobileComponent,
    StartPlaningMobileComponent,
    TextEllipsisComponent,
    SwiperDirective,
    AttractionListMobileComponent,
  ],
})
export class ThingsToDoMobileComponent
  extends PageInfoComponent
  implements OnInit, OnDestroy
{
  public readonly eSvgIcons = ESvgIcons;
  public readonly ERoutes = ERoutes;
  public readonly eInfoType = EInfoType;

  public breadcrumbs: { text: string; path: string }[] = [];
  public menuOpened: boolean[] = [false, false, false];
  public numOfHurt: number = 0;

  public countryResponse: CountryResponse;
  public countryDetails: CountryDetails;
  public cityResponse: CityResponse;
  public cityDetails: CityDetails;
  public voiceMapResponse: VoiceMapResponse;
  public occasionGeniusResponse: OccasionGeniusResponse;
  public isFetching: boolean;
  public categoriesResponse: IViatorCategories = {};

  public anchorSwiperOptions: SwiperOptions = {
    slidesPerView: 'auto',
    slidesOffsetAfter: 16,
    slidesOffsetBefore: 16,
    freeMode: true,
    allowTouchMove: true,
  };

  constructor(
    protected thingsToDoService: ThingsToDoService,
    protected countryInfoTextService: CountryInfoTextService,
    protected countryInfoService: CountryInfoService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected cdr: ChangeDetectorRef,
    protected scrollToIdService: ScrollToIdService,
  ) {
    super(cdr, scrollToIdService);

    combineLatest([
      this.thingsToDoService.countryResponse$,
      this.thingsToDoService.cityResponse$,
      this.thingsToDoService.categoriesResponse$,
      this.thingsToDoService.voiceMapResponse$,
      this.thingsToDoService.occasionGeniusResponse$,
      this.thingsToDoService.isFetching$,
    ])
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(
        ([
          countryResponse,
          cityResponse,
          categoriesResponse,
          voiceMapResponse,
          occasionGeniusResponse,
          isFetching,
        ]) => {
          this.countryResponse = countryResponse;
          this.cityResponse = cityResponse;
          this.categoriesResponse = categoriesResponse;
          this.voiceMapResponse = voiceMapResponse;
          this.occasionGeniusResponse = occasionGeniusResponse;
          this.isFetching = isFetching;
          this.countryDetails =
            this.countryResponse.countryInfo.getFirstCountry();
          this.cityDetails = this.cityResponse.cityInfo.getFirstCity();
          this.cdr.markForCheck();
        },
      );
  }

  ngOnInit() {
    this.breadcrumbs = this.thingsToDoService.generateBreadcrumbs(
      this.route.snapshot.url[0].path,
    );

    this.defaultLocationName = this.breadcrumbs[0].text
      .replace('things-to-do-in-', '')
      .replace(/-/g, ' ');

    this.thingsToDoService.fetchData(this.route.snapshot.url, (breadcrumbs) => {
      this.breadcrumbs = [...breadcrumbs, ...this.breadcrumbs];
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  flyToGalleryItem(item: any): void {
    if (item.coordinates) {
      this.cityResponse.destinations[0].coordinates = item.coordinates;
    }
  }

  public planAPerfectTrip(): void {
    const { name, place_id } = this.cityDetails;
    const countryId = this.countryDetails.place_id;
    this.countryInfoService.setPerfectTripState(place_id, countryId, name);
    this.router.navigate([ERoutes.SLASH, ERoutes.TRIP_PLANNER]).then();
  }
}
